import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { voiceService } from '../services/voiceService';
import { globalAudioManager } from '../services/globalAudioManager';
import { deviceManager } from '../services/deviceManager';
import { apiService } from '../services/apiService';
import { useAuth } from './AuthContext';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [isVoiceEnabled, setIsVoiceEnabled] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [settings, setSettings] = useState({
    micVolume: 75,
    speakerVolume: 80,
    sensitivity: 60,
    wakeWord: 'hey-sarvik',
    autoListen: true,
    notifications: true
  });
  const [stats, setStats] = useState({
    conversationsToday: 0,
    remindersActive: 0,
    voiceHours: 0,
    responseTime: '0ms'
  });
  
  // Global voice visualizer state for persistence across navigation
  const [voiceVisualizerVisible, setVoiceVisualizerVisible] = useState(true);
  const [voiceData, setVoiceData] = useState({
    frequency: new Float32Array(128),
    waveform: new Float32Array(1024),
    volume: 0,
    isActive: false
  });
  const [backgroundMode, setBackgroundMode] = useState(false);
  const [microphoneLevel, setMicrophoneLevel] = useState(0);
  
  // Dashboard state management - centralized for persistence across navigation
  const [dashboardState, setDashboardState] = useState({
    // Query mode states
    isQueryMode: false,
    queryStatus: 'idle', // idle, recording, processing, transcribed, response
    transcribedText: '',
    llmResponse: '',
    queryAudioStream: null,
    
    // Text mode states
    isTextMode: false,
    textQuery: '',
    textResponse: '',
    isTextProcessing: false,
    
    // Background listening state
    isBackgroundListening: (() => {
      const saved = localStorage.getItem('backgroundListening');
      return saved !== null ? JSON.parse(saved) : true;
    })(),
    showButton: false
  });
  
  // Persistent service references to prevent recreation
  const voiceServiceRef = useRef(null);
  const audioManagerRef = useRef(null);
  const deviceManagerRef = useRef(null); // Add reference to DeviceManager
  const isServicesInitialized = useRef(false);
  const persistentVisualizerRef = useRef(null); // Store persistent visualizer instance

  // Initialize app when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      initializeApp();
    } else {
      cleanupApp();
    }
  }, [isAuthenticated, user]);
  
  // Initialize persistent services once (survives navigation)
  useEffect(() => {
    if (!isServicesInitialized.current) {
      initializePersistentServices();
      isServicesInitialized.current = true;
    }
    
    // Cleanup only on app unmount
    return () => {
      if (isServicesInitialized.current) {
        voiceServiceRef.current?.cleanup();
        audioManagerRef.current?.cleanup();
        deviceManagerRef.current?.cleanup(); // Cleanup DeviceManager
        // Cleanup persistent VoiceVisualizer via global cleanup
        if (window.cleanupGlobalVisualizer) {
          window.cleanupGlobalVisualizer();
        }
      }
    };
  }, []); // Empty dependency - initialize once
  
  const initializePersistentServices = async () => {
    try {
      // Initialize device manager for consistent device access
      await deviceManager.initialize();
      deviceManagerRef.current = deviceManager;
      
      // Initialize global audio manager for persistent voice data
      await globalAudioManager.initialize();
      audioManagerRef.current = globalAudioManager;
      
      // Set up persistent voice data listeners
      globalAudioManager.on('voiceData', (data) => {
        setVoiceData(data);
        setMicrophoneLevel(data.volume * 100);
      });
      
      globalAudioManager.on('statusChange', (status) => {
        // Keep voice status in sync across all components
        setConnectionStatus(status === 'listening' ? 'connected' : 'disconnected');
      });
      
      // Listen for window focus changes to maintain state during app switching
      globalAudioManager.on('windowFocus', (hasFocus) => {
        // App focus changed (Alt+Tab, clicked other app) - continue processing
        // Don't change any states, just acknowledge the focus change
        if (hasFocus) {
          // App regained focus - ensure all services are still active
          if (audioManagerRef.current && !audioManagerRef.current.isPersistentMode) {
            audioManagerRef.current.startPersistentProcessing();
          }
        }
        // Note: When losing focus, we don't stop anything - continue seamlessly
      });
      
      // Listen for app switching events to maintain seamless operation
      globalAudioManager.on('appSwitched', (data) => {
        // App switched (Alt+Tab) - maintain all voice processing and visualization
        // data.focused indicates if app gained or lost focus
        // Continue all operations seamlessly regardless of focus state
      });
      
      // Listen for Electron window state changes
      if (window.electronAPI) {
        window.electronAPI.onWindowStateChange?.((state) => {
          setBackgroundMode(state === 'hidden' || state === 'minimized');
          
          // CRITICAL: Continue voice processing regardless of window state
          // This ensures visualization state is preserved during minimize/tray
          if (state === 'minimized' || state === 'hidden') {
            // Don't stop globalAudioManager - keep it running
          } else if (state === 'normal') {
            // Ensure processing is still active
            if (audioManagerRef.current && !audioManagerRef.current.isPersistentMode) {
              audioManagerRef.current.startPersistentProcessing();
            }
          }
        });
        
        // Listen for voice status changes from Electron
        window.electronAPI.onVoiceStatusChange?.((isActive) => {
          // Update global state to match Electron's voice status
        });
      }
      
      voiceServiceRef.current = voiceService;
      console.log('Persistent voice services initialized');
    } catch (error) {
      console.error('Failed to initialize persistent services:', error);
    }
  };

  const initializeApp = async () => {
    try {
      setConnectionStatus('connecting');
      
      // Initialize voice service
      const voiceInitialized = await voiceService.initialize();
      if (voiceInitialized) {
        setIsVoiceEnabled(true);
      }
      
      // Load user settings
      await loadUserSettings();
      
      // Load user stats
      await loadUserStats();
      
      // Start background voice listening if enabled
      if (settings.autoListen && voiceInitialized) {
        await voiceService.startBackgroundListening();
      }
      
      setConnectionStatus('connected');
      console.log('App initialized successfully');
    } catch (error) {
      console.error('Failed to initialize app:', error);
      setConnectionStatus('disconnected');
    }
  };

  const cleanupApp = () => {
    // Note: Don't cleanup persistent services here - they survive navigation
    // Only cleanup user-specific state
    setIsVoiceEnabled(false);
    setConnectionStatus('disconnected');
  };

  const loadUserSettings = async () => {
    try {
      const userSettings = await apiService.getSettings();
      setSettings(prev => ({ ...prev, ...userSettings }));
    } catch (error) {
      console.error('Failed to load user settings:', error);
    }
  };

  const loadUserStats = async () => {
    try {
      // This would load actual stats from the API
      // For now, we'll use mock data
      setStats({
        conversationsToday: 12,
        remindersActive: 3,
        voiceHours: 2.5,
        responseTime: '245ms'
      });
    } catch (error) {
      console.error('Failed to load user stats:', error);
    }
  };

  const updateSettings = async (newSettings) => {
    try {
      const updatedSettings = { ...settings, ...newSettings };
      await apiService.updateSettings(updatedSettings);
      setSettings(updatedSettings);
      
      // Apply voice settings if they changed
      if (newSettings.micVolume || newSettings.speakerVolume || newSettings.sensitivity) {
        await voiceService.updateVoiceSettings(updatedSettings);
      }
      
      return true;
    } catch (error) {
      console.error('Failed to update settings:', error);
      return false;
    }
  };

  const toggleVoiceListening = async () => {
    try {
      // Use the global audio manager for immediate response
      if (globalAudioManager.isPersistentMode) {
        globalAudioManager.stopPersistentProcessing();
      } else {
        await globalAudioManager.initialize();
        globalAudioManager.startPersistentProcessing();
      }
      
      // Also update the voice service
      if (voiceService.isRecording) {
        await voiceService.stopBackgroundListening();
      } else {
        await voiceService.startBackgroundListening();
      }
      return true;
    } catch (error) {
      console.error('Failed to toggle voice listening:', error);
      return false;
    }
  };

  const getPersistentVisualizerRef = () => {
    return persistentVisualizerRef;
  };

  const testMicrophone = async () => {
    try {
      return await voiceService.testMicrophone();
    } catch (error) {
      console.error('Microphone test failed:', error);
      return false;
    }
  };
  
  // Helper function to update dashboard state
  const updateDashboardState = (updates) => {
    setDashboardState(prev => ({
      ...prev,
      ...updates
    }));
  };

  const toggleVoiceVisualizer = () => {
    setVoiceVisualizerVisible(prev => !prev);
  };
  
  const getAudioDevices = async () => {
    try {
      // Use DeviceManager to get current devices
      const devices = await voiceService.getAudioDevices();
      return devices;
    } catch (error) {
      console.error('Failed to get audio devices:', error);
      return { microphones: [], speakers: [] };
    }
  };

  const selectInputDevice = async (deviceId) => {
    try {
      // Use DeviceManager to select input device
      await deviceManager.setInputDevice(deviceId);
      return true;
    } catch (error) {
      console.error('Failed to select input device:', error);
      return false;
    }
  };

  const selectOutputDevice = async (deviceId) => {
    try {
      // Use DeviceManager to select output device
      await deviceManager.setOutputDevice(deviceId);
      return true;
    } catch (error) {
      console.error('Failed to select output device:', error);
      return false;
    }
  };

  const value = {
    // Existing properties
    isVoiceEnabled,
    connectionStatus,
    settings,
    stats,
    updateSettings,
    toggleVoiceListening,
    testMicrophone,
    isListening: voiceServiceRef.current?.isRecording || false,
    
    // Global voice visualizer state (persists across navigation)
    voiceVisualizerVisible,
    setVoiceVisualizerVisible,
    toggleVoiceVisualizer,
    voiceData,
    setVoiceData,
    backgroundMode,
    microphoneLevel,
    setMicrophoneLevel,
    
    // Dashboard state management (centralized for persistence)
    dashboardState,
    updateDashboardState,
    
    // Device management functions
    getAudioDevices,
    selectInputDevice,
    selectOutputDevice,
    
    // Service references for advanced usage
    voiceService: voiceServiceRef.current,
    audioManager: audioManagerRef.current,
    deviceManager: deviceManagerRef.current, // Add DeviceManager reference
    getPersistentVisualizerRef
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};
